import { useEffect, useMemo, useState } from "react";
import { repoFullName } from "../config";
import { purlFromAlternative, purlsFromAlternatives } from "../data/purlAlternatives";
import { submitEditsAsPR } from "../github/api";
import type { Edit, GitHubUser, PackageEntry } from "../data/types";
import {
  Avatar,
  Btn,
  Glyph,
  StatusPill,
  Theme,
} from "./Primitives";

type Props = {
  theme: Theme;
  edits: Record<string, Edit>;
  packages: Record<string, PackageEntry>;
  ensurePackageDetail: (name: string) => Promise<PackageEntry>;
  onClose: () => void;
  onCommit: () => void;
  isLoggedIn: boolean;
  onRequestLogin: () => void;
  user: GitHubUser | null;
  onSelect: (id: string) => void;
  token: string | null;
};

type Committed = {
  number: number;
  url: string;
  branch: string;
};

export function PRDrawer({
  theme,
  edits,
  packages,
  ensurePackageDetail,
  onClose,
  onCommit,
  isLoggedIn,
  onRequestLogin,
  user,
  onSelect,
  token,
}: Props) {
  const t = theme.t;
  const editEntries = useMemo(() => Object.entries(edits), [edits]);
  const editsCount = editEntries.length;

  const [title, setTitle] = useState(
    "Update PURL mappings for conda-forge packages",
  );
  const [body, setBody] = useState("");
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committed, setCommitted] = useState<Committed | null>(null);
  const [detailPackages, setDetailPackages] = useState<Record<string, PackageEntry>>({});
  const [loadingDetails, setLoadingDetails] = useState(false);

  const allPackages = useMemo(
    () => ({ ...packages, ...detailPackages }),
    [packages, detailPackages],
  );

  const editedPackageNames = useMemo(() => editEntries.map(([id]) => id), [editEntries]);

  useEffect(() => {
    let cancelled = false;
    const missing = editedPackageNames.filter((name) => !allPackages[name]);
    if (missing.length === 0) return;
    setLoadingDetails(true);
    Promise.all(missing.map((name) => ensurePackageDetail(name)))
      .then((loaded) => {
        if (cancelled) return;
        setDetailPackages((prev) => {
          const next = { ...prev };
          for (const pkg of loaded) next[pkg.name] = pkg;
          return next;
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => {
        if (!cancelled) setLoadingDetails(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allPackages, editedPackageNames, ensurePackageDetail]);

  function generateBody(packagesForBody = allPackages): string {
    const lines: string[] = [];
    // Headline summary: counts by transition so a reviewer can size up the
    // PR at a glance before scrolling.
    const stats = { approved: 0, overrode: 0, unmapped: 0, updated: 0, cpes: 0 };
    const bullets: string[] = [];

    for (const [id, e] of editEntries) {
      const pkg = packagesForBody[id];
      if (!pkg) continue;

      // "Before" = whatever the merged dataset currently shows for this
      // package (the user is editing on top of that). The auto guess is
      // kept as a secondary reference when the merged state is a manual
      // override.
      const beforePrimary = pkg.purl ?? null;
      const beforeAlts = purlsFromAlternatives(
        pkg.alternative_purls ?? pkg.auto?.alternative_purls,
      ).filter((p) => p !== beforePrimary);
      const beforeSet = new Set<string>();
      if (beforePrimary) beforeSet.add(beforePrimary);
      for (const a of beforeAlts) beforeSet.add(a);

      const afterPrimary = e.unmapped ? null : e.purl || null;
      const afterAlts = e.unmapped ? [] : e.alternative_purls;
      const afterSet = new Set<string>();
      if (afterPrimary) afterSet.add(afterPrimary);
      for (const a of afterAlts) afterSet.add(a);

      const added = [...afterSet].filter((p) => !beforeSet.has(p));
      const removed = [...beforeSet].filter((p) => !afterSet.has(p));
      const primaryChanged = (beforePrimary || "") !== (afterPrimary || "");
      const matchesAuto =
        !!pkg.auto?.purl && afterPrimary === pkg.auto.purl;

      // CPE diff — only present when the edit touched CPEs (e.cpes defined).
      const beforeCpes = pkg.cpes ?? [];
      const afterCpes = e.cpes ?? beforeCpes;
      const beforeCpeSet = new Set(beforeCpes);
      const afterCpeSet = new Set(afterCpes);
      const cpeAdded = afterCpes.filter((c) => !beforeCpeSet.has(c));
      const cpeRemoved = beforeCpes.filter((c) => !afterCpeSet.has(c));
      const purlChanged = added.length > 0 || removed.length > 0 || primaryChanged;

      // Classify the transition for the headline counter and prefix tag.
      let tag = "approved auto guess";
      if (e.unmapped) {
        stats.unmapped++;
        tag = "marked as no-PURL";
      } else if (pkg.status === "verified" && purlChanged) {
        stats.updated++;
        tag = "updated verified mapping";
      } else if (pkg.auto?.purl && !matchesAuto && purlChanged) {
        stats.overrode++;
        tag = "overrode auto guess";
      } else if (matchesAuto && (added.length || removed.length)) {
        stats.updated++;
        tag = "approved auto guess + added alternates";
      } else if (!purlChanged && (cpeAdded.length || cpeRemoved.length)) {
        stats.cpes++;
        tag = "updated CPEs";
      } else {
        stats.approved++;
        tag = "approved auto guess";
      }

      const sourceUrl = pkg.repo || pkg.homepage || pkg.source_url || null;
      const sourceSnippet = sourceUrl
        ? ` · upstream: ${sourceUrl.replace(/^https?:\/\//, "")}`
        : "";
      bullets.push(
        `- **${pkg.name}** v${pkg.version} — ${tag}${sourceSnippet}`,
      );

      // PURL diff: explicit -/+ lines with role (primary / alternative).
      for (const purl of removed) {
        bullets.push(`  - \`-\` \`${purl}\``);
      }
      for (const purl of added) {
        const role =
          purl === afterPrimary ? "primary" : "alternative";
        bullets.push(`  - \`+\` \`${purl}\` (${role})`);
      }
      // Kept PURLs — only mention if the primary stayed the same and there
      // are alternates; this is the noisiest line so cap to once per pkg.
      const kept = [...afterSet].filter((p) => beforeSet.has(p));
      if (kept.length > 0 && (added.length || removed.length)) {
        const keptStr = kept
          .map((p) => `\`${p}\`${p === afterPrimary ? " (primary)" : ""}`)
          .join(", ");
        bullets.push(`  - unchanged: ${keptStr}`);
      }
      if (e.unmapped) {
        bullets.push(`  - now: _unmapped_`);
      }
      for (const cpe of cpeRemoved) {
        bullets.push(`  - \`-\` \`${cpe}\` (cpe)`);
      }
      for (const cpe of cpeAdded) {
        bullets.push(`  - \`+\` \`${cpe}\` (cpe)`);
      }
      if (e.note) bullets.push(`  - _${e.note}_`);
    }

    const total = editEntries.length;
    const parts: string[] = [];
    if (stats.approved) parts.push(`${stats.approved} approved`);
    if (stats.overrode) parts.push(`${stats.overrode} overrode auto`);
    if (stats.updated) parts.push(`${stats.updated} updated existing`);
    if (stats.cpes) parts.push(`${stats.cpes} updated CPEs`);
    if (stats.unmapped) parts.push(`${stats.unmapped} marked no-PURL`);

    lines.push(
      `This PR updates PURL mappings for **${total}** conda-forge package${total === 1 ? "" : "s"}` +
        (parts.length ? ` — ${parts.join(", ")}.` : "."),
    );
    lines.push("");
    lines.push(...bullets);
    return lines.join("\n");
  }

  async function handleCommit(): Promise<void> {
    if (!isLoggedIn || !token || !user) {
      onRequestLogin();
      return;
    }
    setCommitting(true);
    setError(null);
    try {
      const loaded = await Promise.all(
        editedPackageNames
          .filter((name) => !allPackages[name])
          .map((name) => ensurePackageDetail(name)),
      );
      const packagesForSubmit = { ...allPackages };
      for (const pkg of loaded) packagesForSubmit[pkg.name] = pkg;
      const result = await submitEditsAsPR({
        token,
        user: { login: user.login },
        packages: Object.values(packagesForSubmit),
        edits,
        title,
        body: body.trim() === "" ? generateBody(packagesForSubmit) : body,
      });
      setCommitted({
        number: result.pr.number,
        url: result.pr.html_url,
        branch: result.branch,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCommitting(false);
    }
  }

  function handleClose(): void {
    if (committed) onCommit();
    onClose();
  }

  return (
    <>
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,15,36,0.32)",
          zIndex: 50,
          animation: "fadeIn 200ms ease-out",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 540,
          maxWidth: "100vw",
          background: t.surface,
          borderLeft: `1px solid ${t.border}`,
          zIndex: 51,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-12px 0 40px rgba(0,15,36,0.12)",
          animation: "slideIn 280ms cubic-bezier(.2,.8,.2,1)",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${t.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: t.surface,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Glyph name="pr" size={16} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.fg1 }}>
                Staged changes
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: t.fg2,
                  fontFamily: "JetBrains Mono, monospace",
                }}
              >
                {repoFullName}
              </div>
            </div>
          </div>
          <button onClick={handleClose} style={iconBtn(theme)}>
            <Glyph name="close" size={14} />
          </button>
        </div>

        {committed ? (
          <CommittedView
            theme={theme}
            committed={committed}
            onClose={handleClose}
          />
        ) : (
          <>
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px" }}>
              {loadingDetails ? (
                <div
                  style={{
                    padding: 30,
                    textAlign: "center",
                    color: t.fg3,
                    fontSize: 13,
                  }}
                >
                  Loading package details…
                </div>
              ) : editEntries.length === 0 ? (
                <div
                  style={{
                    padding: 30,
                    textAlign: "center",
                    color: t.fg3,
                    fontSize: 13,
                  }}
                >
                  No edits yet. Edit a mapping on the right to stage a change.
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: t.fg2,
                      fontWeight: 600,
                      letterSpacing: ".04em",
                      textTransform: "uppercase",
                      marginBottom: 2,
                    }}
                  >
                    {editsCount} change{editsCount === 1 ? "" : "s"}
                  </div>
                  {editEntries.map(([id, e]) => {
                    const pkg = allPackages[id];
                    if (!pkg) return null;

                    // "Before" PURLs: whatever the package currently presents.
                    const beforePurls: string[] = [];
                    const beforePrimary = pkg.purl ?? pkg.auto?.purl;
                    if (beforePrimary) beforePurls.push(beforePrimary);
                    for (const alt of pkg.alternative_purls ??
                      pkg.auto?.alternative_purls ??
                      []) {
                      const purl = purlFromAlternative(alt);
                      if (purl !== beforePrimary) beforePurls.push(purl);
                    }

                    const afterPurls = e.unmapped
                      ? ["(unmapped)"]
                      : [e.purl, ...e.alternative_purls];

                    const beforeSet = new Set(beforePurls);
                    const afterSet = new Set(afterPurls);

                    // CPE diff lines — only when the edit touched CPEs.
                    const beforeCpes = pkg.cpes ?? [];
                    const afterCpes = e.cpes ?? beforeCpes;
                    const beforeCpeSet = new Set(beforeCpes);
                    const afterCpeSet = new Set(afterCpes);
                    const cpeRemoved = beforeCpes.filter(
                      (c) => !afterCpeSet.has(c),
                    );
                    const cpeAdded = afterCpes.filter(
                      (c) => !beforeCpeSet.has(c),
                    );

                    return (
                      <div
                        key={id}
                        style={{
                          background: t.surface2,
                          border: `1px solid ${t.border}`,
                          borderRadius: 10,
                          padding: "10px 12px",
                          cursor: "pointer",
                        }}
                        onClick={() => onSelect(id)}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 6,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <code
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: t.fg1,
                                fontFamily: "JetBrains Mono, monospace",
                              }}
                            >
                              {pkg.name}
                            </code>
                            <span style={{ fontSize: 10.5, color: t.fg3 }}>
                              v{pkg.version}
                            </span>
                          </div>
                          <StatusPill status="edited" theme={theme} />
                        </div>
                        <div
                          style={{
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11.5,
                            lineHeight: 1.6,
                          }}
                        >
                          {/* Removed: PURLs that were in the auto/old set
                              but won't be in the new set. */}
                          {beforePurls
                            .filter((p) => !afterSet.has(p))
                            .map((purl, i) => (
                              <div
                                key={`b-${i}`}
                                style={{
                                  color: theme.dark ? "#ff8e6a" : "#a8401b",
                                  background: theme.dark
                                    ? "rgba(255,142,106,.08)"
                                    : "#ffece5",
                                  padding: "2px 6px",
                                  borderRadius: 3,
                                  marginBottom: 2,
                                }}
                              >
                                <span style={{ opacity: 0.7 }}>−</span> {purl}
                              </div>
                            ))}
                          {beforePurls.length === 0 && !e.unmapped && (
                            <div
                              style={{
                                color: theme.dark ? "#ff8e6a" : "#a8401b",
                                background: theme.dark
                                  ? "rgba(255,142,106,.08)"
                                  : "#ffece5",
                                padding: "2px 6px",
                                borderRadius: 3,
                                marginBottom: 2,
                                opacity: 0.7,
                                fontStyle: "italic",
                              }}
                            >
                              <span style={{ opacity: 0.7 }}>−</span> (no
                              previous mapping)
                            </div>
                          )}
                          {/* Added / kept: each PURL in the new set with a
                              role badge so it's obvious there are multiple. */}
                          {afterPurls.map((purl, idx) => {
                            const isNew = !beforeSet.has(purl);
                            const isPrimary = idx === 0 && !e.unmapped;
                            return (
                              <div
                                key={`a-${idx}`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  color: theme.dark ? "#9adf6d" : "#5b9b2c",
                                  background: theme.dark
                                    ? "rgba(154,223,109,.08)"
                                    : "#ecf5dc",
                                  padding: "2px 6px",
                                  borderRadius: 3,
                                  marginTop: 2,
                                  opacity: isNew ? 1 : 0.7,
                                }}
                              >
                                <span style={{ opacity: 0.7 }}>+</span>
                                <span>{purl}</span>
                                {!e.unmapped && (
                                  <span
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 700,
                                      letterSpacing: ".06em",
                                      textTransform: "uppercase",
                                      color: t.fg3,
                                      marginLeft: "auto",
                                    }}
                                  >
                                    {isPrimary ? "primary" : "alt"}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {cpeRemoved.map((cpe, i) => (
                            <div
                              key={`cr-${i}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                color: theme.dark ? "#ff8e6a" : "#a8401b",
                                background: theme.dark
                                  ? "rgba(255,142,106,.08)"
                                  : "#ffece5",
                                padding: "2px 6px",
                                borderRadius: 3,
                                marginTop: 2,
                              }}
                            >
                              <span style={{ opacity: 0.7 }}>−</span>
                              <span>{cpe}</span>
                              <span
                                style={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  letterSpacing: ".06em",
                                  textTransform: "uppercase",
                                  color: t.fg3,
                                  marginLeft: "auto",
                                }}
                              >
                                cpe
                              </span>
                            </div>
                          ))}
                          {cpeAdded.map((cpe, i) => (
                            <div
                              key={`ca-${i}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                color: theme.dark ? "#9adf6d" : "#5b9b2c",
                                background: theme.dark
                                  ? "rgba(154,223,109,.08)"
                                  : "#ecf5dc",
                                padding: "2px 6px",
                                borderRadius: 3,
                                marginTop: 2,
                              }}
                            >
                              <span style={{ opacity: 0.7 }}>+</span>
                              <span>{cpe}</span>
                              <span
                                style={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  letterSpacing: ".06em",
                                  textTransform: "uppercase",
                                  color: t.fg3,
                                  marginLeft: "auto",
                                }}
                              >
                                cpe
                              </span>
                            </div>
                          ))}
                        </div>
                        {e.note && (
                          <div
                            style={{
                              fontSize: 11.5,
                              color: t.fg2,
                              marginTop: 6,
                              fontStyle: "italic",
                            }}
                          >
                            "{e.note}"
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {editEntries.length > 0 && (
              <div
                style={{
                  borderTop: `1px solid ${t.border}`,
                  padding: "14px 20px",
                  background: t.surface2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: t.fg2,
                      letterSpacing: ".04em",
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    PR title
                  </div>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    style={{
                      width: "100%",
                      background: t.surface,
                      color: t.fg1,
                      border: `1px solid ${t.border}`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontFamily: "Inter, sans-serif",
                      outline: "none",
                    }}
                  />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: t.fg2,
                      letterSpacing: ".04em",
                      textTransform: "uppercase",
                      marginBottom: 4,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>PR description</span>
                    <button
                      onClick={() => setBody(generateBody())}
                      style={{
                        background: "transparent",
                        border: 0,
                        color: t.link,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      auto-generate
                    </button>
                  </div>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="(optional — auto-generated from your changes if blank)"
                    style={{
                      width: "100%",
                      background: t.surface,
                      color: t.fg1,
                      border: `1px solid ${t.border}`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 12.5,
                      fontFamily: "JetBrains Mono, monospace",
                      outline: "none",
                      minHeight: 80,
                      resize: "vertical",
                    }}
                  />
                </div>

                {error && (
                  <div
                    style={{
                      fontSize: 12,
                      color: t.bad,
                      background: theme.dark ? "#3a1f1f" : "#ffe5dc",
                      padding: "8px 10px",
                      borderRadius: 6,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {error}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    marginTop: 4,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      fontSize: 11.5,
                      color: t.fg2,
                    }}
                  >
                    {isLoggedIn && user ? (
                      <>
                        <Avatar user={user} size={20} />
                        <span>
                          commits as <strong>@{user.login}</strong>
                        </span>
                      </>
                    ) : (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <Glyph name="github" size={13} />
                        Sign in to push a PR
                      </span>
                    )}
                  </div>
                  <Btn
                    theme={theme}
                    variant="primary"
                    icon="pr"
                    disabled={committing || loadingDetails}
                    onClick={handleCommit}
                  >
                    {committing
                      ? "Pushing branch…"
                      : isLoggedIn
                        ? `Create PR with ${editsCount} change${editsCount === 1 ? "" : "s"}`
                        : "Sign in & create PR"}
                  </Btn>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function CommittedView({
  theme,
  committed,
  onClose,
}: {
  theme: Theme;
  committed: Committed;
  onClose: () => void;
}) {
  const t = theme.t;
  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "30px 24px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: t.accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 14,
        }}
      >
        <Glyph name="check" size={28} stroke={2} />
      </div>
      <h3
        style={{
          fontFamily: "Moranga, serif",
          fontWeight: 300,
          fontSize: 26,
          margin: "0 0 6px",
        }}
      >
        PR opened!
      </h3>
      <div style={{ fontSize: 13, color: t.fg2, marginBottom: 18 }}>
        Your changes are pushed and waiting for review.
      </div>
      <div
        style={{
          background: t.surface2,
          border: `1px solid ${t.border}`,
          borderRadius: 10,
          padding: 14,
          textAlign: "left",
          maxWidth: 380,
          margin: "0 auto 18px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <Glyph name="pr" size={14} />
          <code
            style={{ fontSize: 12.5, fontWeight: 600, color: t.fg1 }}
          >
            #{committed.number}
          </code>
          <span style={{ fontSize: 11, color: t.fg2 }}>open</span>
        </div>
        <div
          style={{
            fontSize: 11,
            color: t.fg2,
            fontFamily: "JetBrains Mono, monospace",
            marginBottom: 4,
          }}
        >
          branch
        </div>
        <div
          style={{
            fontSize: 12,
            fontFamily: "JetBrains Mono, monospace",
            color: t.fg1,
            wordBreak: "break-all",
          }}
        >
          {committed.branch}
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <a
          href={committed.url}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "#001d38",
            color: t.accent,
            textDecoration: "none",
            padding: "8px 14px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Glyph name="github" size={14} />
          View on GitHub
        </a>
        <Btn theme={theme} variant="ghost" onClick={onClose}>
          Done
        </Btn>
      </div>
    </div>
  );
}

function iconBtn(theme: Theme) {
  const t = theme.t;
  return {
    background: t.surface2,
    border: `1px solid ${t.border}`,
    color: t.fg1,
    borderRadius: 6,
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  } as const;
}
